import { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { X, ArrowRightLeft, Loader2 } from 'lucide-react';
import { transfersApi } from '@/api/transfersApi';
import { locationsApi } from '@/api/locationsApi';
import { inventoryApi } from '@/api/inventoryApi';
import { handleApiError } from '@/api/client';
import { ErrorAlert } from '@/components/common/ErrorAlert';

interface RecordTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function RecordTransferModal({ isOpen, onClose, onSuccess }: RecordTransferModalProps) {
  const queryClient = useQueryClient();
  const [fromLocationId, setFromLocationId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [productVariantId, setProductVariantId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: locationsApi.getAll,
  });

  const { data: inventory } = useQuery({
    queryKey: ['inventory', fromLocationId],
    queryFn: () => fromLocationId ? inventoryApi.getVariantsByLocation(fromLocationId) : Promise.resolve([]),
    enabled: !!fromLocationId,
  });

  const transferMutation = useMutation({
    mutationFn: transfersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      onSuccess?.();
      handleClose();
    },
    onError: (err: any) => {
      setError(handleApiError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!fromLocationId || !toLocationId || !productVariantId || !quantity) {
      setError('Please fill in all required fields');
      return;
    }

    if (fromLocationId === toLocationId) {
      setError('From and To locations must be different');
      return;
    }

    const qty = parseInt(quantity);
    if (isNaN(qty) || qty < 1) {
      setError('Quantity must be at least 1');
      return;
    }

    transferMutation.mutate({
      productVariantId,
      fromLocationId,
      toLocationId,
      quantity: qty,
      notes: notes || undefined,
    });
  };

  const handleClose = () => {
    setFromLocationId('');
    setToLocationId('');
    setProductVariantId('');
    setQuantity('1');
    setNotes('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/60 dark:bg-charcoal-950/80 backdrop-blur-sm" onClick={handleClose} />
      
      <div className="relative w-full max-w-lg card animate-scale-in">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-charcoal-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-450/10 flex items-center justify-center">
              <ArrowRightLeft className="w-5 h-5 text-amber-450" />
            </div>
            <div>
              <h2 className="font-sans text-xl font-semibold text-gray-900 dark:text-warm-cream">Record Transfer</h2>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Move inventory between locations</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 rounded-lg">
            <X className="w-5 h-5 text-gray-600 dark:text-warm-sand" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <ErrorAlert message={error} />
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                From Location <span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <select
                value={fromLocationId}
                onChange={(e) => {
                  setFromLocationId(e.target.value);
                  setProductVariantId('');
                }}
                className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream focus:border-amber-450 focus:outline-none"
              >
                <option value="">Select location...</option>
                {locations?.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                To Location <span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <select
                value={toLocationId}
                onChange={(e) => setToLocationId(e.target.value)}
                className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream focus:border-amber-450 focus:outline-none"
              >
                <option value="">Select location...</option>
                {locations?.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
              Product <span className="text-red-600 dark:text-red-400">*</span>
            </label>
            <select
              value={productVariantId}
              onChange={(e) => setProductVariantId(e.target.value)}
              disabled={!fromLocationId || !inventory}
              className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream focus:border-amber-450 focus:outline-none disabled:opacity-50"
            >
              <option value="">
                {!fromLocationId 
                  ? 'Select a source location first' 
                  : !inventory 
                    ? 'Loading...' 
                    : inventory.length === 0 
                      ? 'No inventory at this location' 
                      : 'Select product...'
                }
              </option>
              {inventory?.filter(item => item.availableQuantity > 0).map((item) => (
                <option key={item.variantId} value={item.variantId}>
                  {item.productName} - {item.variantSku} (Available: {item.availableQuantity})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
              Quantity <span className="text-red-600 dark:text-red-400">*</span>
            </label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream focus:border-amber-450 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes about this transfer..."
              rows={2}
              className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream focus:border-amber-450 focus:outline-none resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-charcoal-700">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={transferMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={transferMutation.isPending}
            >
              {transferMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recording...
                </>
              ) : (
                <>
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Record Transfer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
